import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { GraduationCap, Award, Linkedin, BookOpen, Quote, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function InitialsAvatar({ name, size = 80 }) {
    const initials = (name || "?")
        .replace(/^(Pr\.|Dr\.|M\.|Mme)\s+/i, "")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join("");
    return (
        <div
            className="flex items-center justify-center rounded-full bg-gradient-to-br from-brand-orange/15 to-brand-orange/5 font-display text-brand-orange ring-2 ring-brand-orange/20"
            style={{ width: size, height: size, fontSize: size * 0.36 }}
            aria-hidden
        >
            {initials}
        </div>
    );
}

function MemberCard({ m, idx }) {
    return (
        <motion.article
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.55, delay: (idx % 3) * 0.07 }}
            className="rounded-2xl border border-brand-line bg-white p-7 shadow-premium transition-all hover:-translate-y-1 hover:shadow-lg"
            data-testid={`comite-member-card-${idx}`}
        >
            <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
                {m.photo_url ? (
                    <img
                        src={m.photo_url}
                        alt={m.full_name}
                        className="h-20 w-20 flex-shrink-0 rounded-full object-cover ring-2 ring-brand-orange/20"
                    />
                ) : (
                    <InitialsAvatar name={m.full_name} size={80} />
                )}
                <div className="min-w-0 flex-1">
                    <h3 className="font-display text-xl leading-tight text-brand-ink">{m.full_name}</h3>
                    <p className="mt-1.5 text-xs font-medium uppercase tracking-wider text-brand-orange">
                        {m.title}
                    </p>
                </div>
                {m.linkedin_url ? (
                    <a
                        href={m.linkedin_url}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-full border border-brand-line p-2 text-brand-smoke transition-colors hover:border-brand-ink hover:text-brand-ink"
                        aria-label={`LinkedIn ${m.full_name}`}
                    >
                        <Linkedin className="h-3.5 w-3.5" />
                    </a>
                ) : null}
            </div>

            <p className="mt-5 text-sm leading-relaxed text-brand-ink/75">
                {m.bio || "Biographie en cours de validation."}
            </p>

            {m.expertises?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                    {m.expertises.map((e) => (
                        <span
                            key={e}
                            className="rounded-full border border-brand-orange/30 bg-brand-orange/5 px-2.5 py-0.5 text-[10px] font-medium text-brand-ink"
                        >
                            {e}
                        </span>
                    ))}
                </div>
            )}

            {m.affiliations?.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-1.5">
                    {m.affiliations.map((a) => (
                        <span
                            key={a}
                            className="inline-flex items-center gap-1 rounded-full border border-brand-ink/15 bg-white px-2.5 py-0.5 text-[10px] font-medium text-brand-ink/80"
                        >
                            <Award className="h-2.5 w-2.5 text-brand-orange" /> {a}
                        </span>
                    ))}
                </div>
            )}

            {m.bio_pending && (
                <span className="mt-4 inline-block rounded-full border border-brand-line bg-[#FAFAFA] px-2.5 py-0.5 text-[10px] font-medium text-brand-smoke">
                    Bio en cours de validation
                </span>
            )}
        </motion.article>
    );
}

function TestimonialCard({ m, idx }) {
    return (
        <motion.figure
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, delay: (idx % 3) * 0.08 }}
            className="relative flex h-full flex-col rounded-2xl border border-brand-line bg-white p-7 shadow-premium"
            data-testid={`comite-temoignage-card-${idx}`}
        >
            <Quote
                className="absolute right-6 top-6 h-8 w-8 text-brand-orange/15"
                aria-hidden="true"
            />
            <blockquote className="flex-1 pr-6 text-[15px] leading-[1.7] text-brand-ink/85">
                <span className="italic">« {m.temoignage_pa} »</span>
            </blockquote>
            <figcaption className="mt-6 flex items-center gap-4 border-t border-brand-line pt-5">
                {m.photo_url ? (
                    <img
                        src={m.photo_url}
                        alt={m.full_name}
                        className="h-11 w-11 flex-shrink-0 rounded-full object-cover ring-1 ring-brand-orange/25"
                    />
                ) : (
                    <InitialsAvatar name={m.full_name} size={44} />
                )}
                <div className="min-w-0 flex-1">
                    <div className="truncate font-display text-sm text-brand-ink">
                        {m.full_name}
                    </div>
                    <div className="truncate text-[11px] font-medium uppercase tracking-wider text-brand-smoke">
                        {m.title}
                    </div>
                </div>
            </figcaption>
            <div className="mt-4">
                <span
                    className="inline-flex items-center gap-1 rounded-full border border-brand-orange/30 bg-brand-orange/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-orange"
                    data-testid={`comite-temoignage-pending-${idx}`}
                >
                    <Clock className="h-2.5 w-2.5" /> En cours de validation
                </span>
            </div>
        </motion.figure>
    );
}

export default function NotreComiteScientifique() {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        document.title = "Notre comité scientifique · Cursus Physio";
        const meta = document.querySelector('meta[name="description"]') || (() => {
            const m = document.createElement("meta");
            m.name = "description";
            document.head.appendChild(m);
            return m;
        })();
        meta.content =
            "Le comité scientifique Cursus Physio : médecins hospitalo-universitaires, spécialistes du sport, de la gériatrie et de la nutrition garantissent la rigueur de nos enseignements.";

        api.get("/comite-scientifique")
            .then((r) => setMembers(r.data || []))
            .catch((e) => setErr(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    return (
        <main className="bg-white" data-testid="page-comite-scientifique">
            <section className="border-b border-brand-line bg-[#33333A] px-6 py-20 text-white sm:py-28">
                <div className="mx-auto max-w-7xl">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-orange/40 bg-brand-orange/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-orange">
                        <BookOpen className="h-3 w-3" /> Garants scientifiques
                    </span>
                    <h1 className="mt-6 font-display text-5xl leading-[0.95] sm:text-6xl lg:text-7xl">
                        Notre <em className="italic text-white/65">comité scientifique</em>.
                    </h1>
                    <p className="mt-6 max-w-2xl text-base text-white/70 sm:text-lg">
                        Médecins hospitalo-universitaires, spécialistes du sport, de la gériatrie, de la
                        nutrition. Le comité scientifique de Cursus Physio garantit la{" "}
                        <strong className="text-white">rigueur, l'actualité et la légitimité</strong>{" "}
                        de chaque contenu enseigné.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-6 py-16 sm:py-20">
                {loading ? (
                    <div className="py-20 text-center text-sm text-brand-smoke">Chargement…</div>
                ) : err ? (
                    <div className="py-20 text-center text-sm text-red-600">{err}</div>
                ) : (
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="comite-grid">
                        {members.map((m, idx) => (
                            <MemberCard key={m.id} m={m} idx={idx} />
                        ))}
                    </div>
                )}
            </section>

            {/* ============ Ce qu'ils pensent de la Prévention Active ============ */}
            {!loading && !err && members.some((m) => m.temoignage_pa) && (
                <section
                    className="border-t border-brand-line bg-brand-sand px-6 py-20 sm:py-28"
                    data-testid="section-comite-temoignages-pa"
                >
                    <div className="mx-auto max-w-7xl">
                        <div className="max-w-3xl">
                            <span
                                className="inline-flex items-center gap-1.5 rounded-full border border-brand-orange/30 bg-brand-orange/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-orange"
                                data-testid="comite-temoignages-eyebrow"
                            >
                                <Quote className="h-3 w-3" /> Regards croisés
                            </span>
                            <h2
                                className="mt-6 font-display text-[36px] leading-[1.06] tracking-tightest text-brand-ink sm:text-5xl lg:text-[56px]"
                                data-testid="comite-temoignages-title"
                            >
                                Ce qu&apos;ils pensent de la{" "}
                                <em className="italic text-brand-orange">Prévention Active</em>.
                            </h2>
                            <p className="mt-5 max-w-2xl text-base leading-relaxed text-brand-ink/75">
                                Six regards de médecins, chercheurs et cliniciens sur le rôle
                                socio-économique de la Prévention Active — et sur ses vertus pour les
                                thérapeutes eux-mêmes. Les citations ci-dessous sont{" "}
                                <strong className="text-brand-ink">en cours de validation</strong>{" "}
                                par leurs auteurs.
                            </p>
                        </div>

                        <div
                            className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
                            data-testid="comite-temoignages-grid"
                        >
                            {members
                                .filter((m) => m.temoignage_pa)
                                .map((m, idx) => (
                                    <TestimonialCard key={m.id} m={m} idx={idx} />
                                ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Trust strip */}
            <section className="border-t border-brand-line bg-[#FAFAFA] px-6 py-14">
                <div className="mx-auto max-w-3xl text-center">
                    <GraduationCap className="mx-auto h-7 w-7 text-brand-orange" />
                    <p className="mt-4 font-display text-2xl text-brand-ink">
                        « Une science exigeante, transmise sans dogme. »
                    </p>
                    <p className="mt-3 text-sm text-brand-smoke">
                        Chaque contenu pédagogique Cursus Physio est validé par notre comité scientifique
                        avant diffusion. C'est notre garantie d'excellence.
                    </p>
                </div>
            </section>
        </main>
    );
}
